import React from "react";

// --- CARD ---
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function Card({ className = "", children, ...props }: CardProps): JSX.Element {
  return (
    <div
      className={`overflow-hidden rounded-lg bg-white shadow-md transition-shadow duration-300 hover:shadow-lg ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

// --- CARD CONTENT ---
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function CardContent({ className = "", children, ...props }: CardContentProps): JSX.Element {
  return (
    // O MUI geralmente aplica um padding bottom maior no último filho
    <div className={`p-4 last:pb-6 ${className}`} {...props}>
      {children}
    </div>
  );
}

// --- CARD MEDIA ---
export interface CardMediaProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  component?: React.ElementType;
  image?: string; // Mantido para compatibilidade com a API do MUI
}

export function CardMedia({
  component: Component = "img",
  image,
  src,
  className = "",
  ...props
}: CardMediaProps): JSX.Element {
  const source = image || src;

  // Se for a tag img padrão
  if (Component === "img") {
    return (
      <img
        src={source}
        className={`w-full object-cover ${className}`}
        {...props}
      />
    );
  }

  // Se for outro elemento (como uma div), aplica a imagem como background (comportamento padrão do MUI quando não é img)
  return (
    <Component
      className={`w-full bg-cover bg-center ${className}`}
      style={{ backgroundImage: source ? `url(${source})` : undefined }}
      {...props}
    />
  );
}

// --- CARD ACTION AREA ---
// Utilizamos a tag button para garantir acessibilidade via teclado (focus)
export interface CardActionAreaProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

export function CardActionArea({
  className = "",
  children,
  ...props
}: CardActionAreaProps): JSX.Element {
  return (
    <button
      type="button"
      className={`block w-full text-left transition-colors hover:bg-black/5 focus:outline-none focus-visible:bg-black/10 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

// --- CARD ACTIONS ---
export interface CardActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function CardActions({ className = "", children, ...props }: CardActionsProps): JSX.Element {
  return (
    <div className={`flex items-center p-2 space-x-2 ${className}`} {...props}>
      {children}
    </div>
  );
}